// Translates every piece of text on the page into the language picked in the top menu.
// Text is sent to our server, which translates it once and remembers it. Switching back to
// English puts the original text back. Anything inside translate="no" or .notranslate is left alone
// (card codes, amounts, emails).
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT", "OPTION", "SVG", "KBD"]);
const ATTRS = ["placeholder", "title", "aria-label"];
const original = new WeakMap();          // text node -> English text
const originalAttr = new WeakMap();      // element -> {attr: English}
const memory = {};                       // lang -> {english: translated}

function loadMemory(lang) {
    if (memory[lang]) return memory[lang];
    try { memory[lang] = JSON.parse(localStorage.getItem(`g2c_tr_${lang}`) || "{}"); } catch { memory[lang] = {}; }
    return memory[lang];
}
function saveMemory(lang) {
    try {
        const m = memory[lang] || {};
        const keys = Object.keys(m);
        if (keys.length > 4000) keys.slice(0, keys.length - 4000).forEach((k) => delete m[k]);
        localStorage.setItem(`g2c_tr_${lang}`, JSON.stringify(m));
    } catch { /* storage full or blocked: still works, just not remembered */ }
}

// Worth translating: has letters, isn't a code, amount, email or link
function wanted(s) {
    const t = s.trim();
    if (t.length < 2 || t.length > 400) return false;
    if (!/[A-Za-z]{2,}/.test(t.replace(/CA\$|US\$|\b(CAD|USD|EUR|CA|US)\b/g, ""))) return false;
    if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(t) || /^https?:\/\//.test(t)) return false;
    if (/^(GC|GTC|EX|SB|MWT|P|CD|TK)-[A-Z0-9]+$/.test(t)) return false;
    if (/^[A-Z0-9-]{8,}$/.test(t)) return false;
    return true;
}

function skipped(el) {
    for (let n = el; n && n !== document.body; n = n.parentElement) {
        if (SKIP_TAGS.has(n.tagName) || n.isContentEditable) return true;
        if (n.getAttribute && (n.getAttribute("translate") === "no" || n.classList?.contains("notranslate"))) return true;
    }
    return false;
}

function collect(root) {
    const nodes = [];
    const attrs = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (n.parentElement && !skipped(n.parentElement)) nodes.push(n);
    }
    const els = root.querySelectorAll ? root.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(",")) : [];
    els.forEach((el) => { if (!skipped(el)) attrs.push(el); });
    return { nodes, attrs };
}

export default function AutoTranslate() {
    const { lang } = useI18n();
    const location = useLocation();

    useEffect(() => {
        let stopped = false;
        let timer = null;
        let applying = false;

        const englishOf = (node) => {
            const cur = node.nodeValue;
            const rec = original.get(node);
            // React changed the text since we translated it: the new value is the new English
            if (!rec || (cur !== rec.shown)) {
                original.set(node, { en: cur, shown: cur });
                return cur;
            }
            return rec.en;
        };

        const restoreAll = () => {
            const { nodes, attrs } = collect(document.body);
            applying = true;
            nodes.forEach((n) => { const r = original.get(n); if (r && n.nodeValue === r.shown && r.shown !== r.en) { n.nodeValue = r.en; r.shown = r.en; } });
            attrs.forEach((el) => { const r = originalAttr.get(el); if (r) ATTRS.forEach((a) => { if (r[a] != null && el.getAttribute(a) === r[`_${a}`]) el.setAttribute(a, r[a]); }); });
            applying = false;
        };

        const run = async () => {
            if (stopped) return;
            if (lang === "en") { restoreAll(); return; }
            const mem = loadMemory(lang);
            const { nodes, attrs } = collect(document.body);
            const need = new Set();
            const jobs = [];
            nodes.forEach((n) => {
                const en = englishOf(n);
                if (wanted(en)) { jobs.push([n, en]); if (!mem[en.trim()]) need.add(en.trim()); }
            });
            const attrJobs = [];
            attrs.forEach((el) => {
                const rec = originalAttr.get(el) || {};
                ATTRS.forEach((a) => {
                    const v = el.getAttribute(a);
                    if (v == null) return;
                    const en = v === rec[`_${a}`] ? rec[a] : v;
                    rec[a] = en;
                    if (wanted(en)) { attrJobs.push([el, a, en]); if (!mem[en.trim()]) need.add(en.trim()); }
                });
                originalAttr.set(el, rec);
            });
            const list = [...need];
            for (let i = 0; i < list.length && !stopped; i += 120) {
                try {
                    const r = await api.post("/i18n/translate", { lang, texts: list.slice(i, i + 120) });
                    Object.assign(mem, r.data?.translations || {});
                } catch { break; }
            }
            if (list.length) saveMemory(lang);
            if (stopped) return;
            applying = true;
            jobs.forEach(([n, en]) => {
                const tr = mem[en.trim()];
                if (!tr || !n.isConnected) return;
                const lead = en.match(/^\s*/)[0];
                const trail = en.match(/\s*$/)[0];
                const shown = lead + tr + trail;
                if (n.nodeValue !== shown) n.nodeValue = shown;
                original.set(n, { en, shown });
            });
            attrJobs.forEach(([el, a, en]) => {
                const tr = mem[en.trim()];
                if (!tr) return;
                if (el.getAttribute(a) !== tr) el.setAttribute(a, tr);
                const rec = originalAttr.get(el) || {};
                rec[`_${a}`] = tr;
                originalAttr.set(el, rec);
            });
            applying = false;
        };

        const schedule = () => { clearTimeout(timer); timer = setTimeout(run, 120); };
        const obs = new MutationObserver(() => { if (!applying) schedule(); });
        obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
        schedule();
        return () => { stopped = true; clearTimeout(timer); obs.disconnect(); };
    }, [lang, location.pathname]);

    return null;
}
